import { defineStore } from 'pinia'
import { supabase } from '../services/supabase'
import { ref } from 'vue'
import { useSubscriptionStore } from './subscription'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const loading = ref(true)
  const error = ref(null)
  const subscriptionStore = useSubscriptionStore()

  // Initialize user session
  async function init() {
    try {
      loading.value = true
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError
      if (session?.user) {
        setUser(session.user)
        await subscriptionStore.initializeUser()
      }
    } catch (err) {
      error.value = err.message
      console.error('Error initializing auth store:', err)
    } finally {
      loading.value = false
    }
  }

  // Call init when store is created
  init()

  async function setUser(userData) {
    user.value = userData
  }

  async function signIn(email, password) {
    try {
      error.value = null
      loading.value = true
      
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (signInError) throw signInError
      
      if (data?.user) {
        user.value = data.user
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Sign in error:', err.message)
      }
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function signUp(email, password) {
    try {
      error.value = null
      loading.value = true
      
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password
      })

      if (signUpError) throw signUpError

      if (data?.user) {
        user.value = data.user
      }

      return { needsEmailVerification: data?.user?.identities?.length === 0 }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Sign up error:', err.message)
      }
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function signOut() {
    try {
      error.value = null
      const { error: signOutError } = await supabase.auth.signOut()
      if (signOutError) throw signOutError
      user.value = null
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Sign out error:', err.message)
      }
      error.value = err.message
      throw err
    }
  }

  async function resetPassword(email) {
    try {
      error.value = null
      loading.value = true
      
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email)
      if (resetError) throw resetError
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Password reset error:', err.message)
      }
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updatePassword(newPassword) {
    try {
      error.value = null
      loading.value = true
      
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (updateError) throw updateError
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Password update error:', err.message)
      }
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function signInWithGoogle() {
    try {
      loading.value = true
      const { data, error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          },
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      if (authError) throw authError
      
      // If we get data back, it means the OAuth window was opened
      if (data) {
        // The actual redirect will be handled by the AuthCallback component
        console.log('Google OAuth initiated successfully')
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    loading,
    error,
    setUser,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    signInWithGoogle
  }
})
